/**
 * Drag-to-scroll for any overflow:auto/scroll panel (mouse + touch).
 * Skips interactive controls so toggles/buttons still work.
 */

const INTERACTIVE =
    'input, textarea, select, button, a, label, option, ' +
    '.custom-checkbox, .checkbox-slider, .map-tab, .z-node, .z-star-sys, ' +
    '.z-mini-planet, .jump-btn, .no-drag, [contenteditable="true"], ' +
    '[role="button"], [role="switch"], [role="slider"]';

function isScrollable(el) {
    if (!el || el.nodeType !== 1) return false;
    const style = getComputedStyle(el);
    const oy = style.overflowY;
    const ox = style.overflowX;
    const yOk =
        (oy === 'auto' || oy === 'scroll' || oy === 'overlay' || el.classList.contains('scroll-drag')) &&
        el.scrollHeight > el.clientHeight + 2;
    const xOk =
        (ox === 'auto' || ox === 'scroll' || ox === 'overlay' || el.classList.contains('scroll-drag')) &&
        el.scrollWidth > el.clientWidth + 2;
    return yOk || xOk;
}

function findScrollable(start) {
    let n = start;
    while (n && n !== document.documentElement) {
        if (isScrollable(n)) return n;
        n = n.parentElement;
    }
    return null;
}

/**
 * Install global drag-scroll once.
 * @returns {() => void} dispose
 */
export function initDragScroll() {
    if (typeof document === 'undefined') return () => {};
    if (document.documentElement.dataset.dragScroll === '1') {
        return () => {};
    }
    document.documentElement.dataset.dragScroll = '1';

    let scroller = null;
    let startY = 0;
    let startX = 0;
    let originTop = 0;
    let originLeft = 0;
    let moved = false;
    let suppressClick = false;

    const onDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest?.(INTERACTIVE)) return;
        // Don't steal multi-touch / canvas game input
        if (e.target.closest?.('#gameCanvas, #joystickArea')) return;

        const s = findScrollable(e.target);
        if (!s) return;

        scroller = s;
        startY = e.clientY;
        startX = e.clientX;
        originTop = s.scrollTop;
        originLeft = s.scrollLeft;
        moved = false;
        suppressClick = false;
        scroller.classList.add('is-dragging');
        try {
            scroller.setPointerCapture?.(e.pointerId);
        } catch {
            /* ignore */
        }
    };

    const onMove = (e) => {
        if (!scroller) return;
        const dy = e.clientY - startY;
        const dx = e.clientX - startX;
        if (!moved && (Math.abs(dy) > 4 || Math.abs(dx) > 4)) {
            moved = true;
            suppressClick = true;
        }
        if (!moved) return;
        scroller.scrollTop = originTop - dy;
        scroller.scrollLeft = originLeft - dx;
        // Prevent text selection while dragging
        e.preventDefault?.();
    };

    const onUp = () => {
        if (!scroller) return;
        scroller.classList.remove('is-dragging');
        scroller = null;
        // Keep suppressClick until after the synthetic click
        if (suppressClick) {
            setTimeout(() => {
                suppressClick = false;
                moved = false;
            }, 0);
        } else {
            moved = false;
        }
    };

    const onClickCapture = (e) => {
        if (!suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
        suppressClick = false;
        moved = false;
    };

    document.addEventListener('pointerdown', onDown, { passive: true, capture: true });
    document.addEventListener('pointermove', onMove, { passive: false, capture: true });
    document.addEventListener('pointerup', onUp, { passive: true, capture: true });
    document.addEventListener('pointercancel', onUp, { passive: true, capture: true });
    document.addEventListener('click', onClickCapture, true);

    return () => {
        document.removeEventListener('pointerdown', onDown, true);
        document.removeEventListener('pointermove', onMove, true);
        document.removeEventListener('pointerup', onUp, true);
        document.removeEventListener('pointercancel', onUp, true);
        document.removeEventListener('click', onClickCapture, true);
        delete document.documentElement.dataset.dragScroll;
    };
}
